import { UsernameForm } from './UsernameForm.js';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';
import { superAvailable, type Choices } from '../../state/website-model.js';
import { GameCard, type LibraryGame } from './LibraryPages.js';
import { signInUrl, type SiteUser } from './WebsiteApp.js';

interface Account {
  user: { id: number; email: string; name: string; username: string | null; avatar: string | null; createdAt: string };
  /** The site's owners get the control panel link. */
  owner?: boolean;
  rank: {
    id: number;
    name: string;
    color: string;
    expiresAt: string | null;
    limits: { messagesPerDay: number | null; tokensPerDay: number | null; tokensPerMonth: number | null; maxConcurrent: number };
  } | null;
  demo: {
    enabled: boolean;
    ok: boolean;
    reason: string | null;
    viaFallback: boolean;
    fallbackReason: string | null;
    usage: { messagesToday: number; tokensToday: number; tokensMonth: number };
    claude: { fiveHour: number; weekly: number; fiveHourLimit: number | null; weeklyLimit: number | null; resets: { fiveHour: string | null; weekly: string | null } } | null;
  };
  choices: Choices;
  /** Limits are null when the account has none. */
  storage: { usedBytes: number; limitBytes: number | null };
  limits: { projects: number | null; maxGameMb: number };
}

function Meter({ label, used, limit, unit = '' }: { label: string; used: number; limit: number | null; unit?: string }) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  const fmt = (n: number) => (unit === '%' ? `${n.toFixed(1)}%` : n.toLocaleString());
  return (
    <div className="meter">
      <div className="meter-head">
        <span>{label}</span>
        <span className="muted">{limit === null ? `${fmt(used)} (no limit)` : `${fmt(used)} of ${fmt(limit)}${unit === '%' ? '' : unit}`}</span>
      </div>
      {limit !== null && (
        <div className="meter-bar">
          <div className={`meter-fill${pct >= 100 ? ' is-full' : pct > 80 ? ' is-high' : ''}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

const mb = (bytes: number) => `${Math.round(bytes / 1048576)} MB`;

/** The account: rank and Prisma usage, storage, published games. */
export function AccountPage({ user, ready, onChanged }: { user: SiteUser | null; ready: boolean; onChanged: () => void }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [games, setGames] = useState<LibraryGame[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setAccount(await api.get<Account>('/account'));
      setGames((await api.get<{ games: LibraryGame[] }>('/library/games?mine=1')).games);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your account.');
    }
  }, []);

  useEffect(() => {
    if (user) void load();
  }, [user, load]);

  if (!ready) return null;
  if (!user)
    return (
      <div className="site-page">
        <a className="primary button-link" href={signInUrl()}>
          Sign in with Google
        </a>
      </div>
    );
  if (!account) return <div className="site-page"><p className="muted">{error ?? 'Loading…'}</p></div>;

  async function deleteGame(slug: string) {
    if (!window.confirm('Remove this game from the library?')) return;
    await api.del(`/library/games/${slug}`).catch(() => undefined);
    await load();
  }

  const { demo, rank } = account;
  return (
    <div className="site-page account-page">
      <div className="account-head">
        {account.user.avatar && <img className="avatar avatar-lg" src={account.user.avatar} alt="" referrerPolicy="no-referrer" />}
        <div>
          <h1>{account.user.username ?? 'New player'}</h1>
          {account.owner && (
            <a className="primary button-link" href="/control/">
              Control panel
            </a>
          )}
          <p className="muted">{account.user.email}</p>
        </div>
        {rank && (
          <span className="rank-badge rank-badge-lg" style={{ borderColor: rank.color, color: rank.color }}>
            {rank.name}
            {rank.expiresAt ? ` until ${new Date(rank.expiresAt).toLocaleDateString()}` : ''}
          </span>
        )}
      </div>
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}

      <div className="account-grid">
        <section className="site-card prisma-card">
          <h2>Prisma</h2>
          <p className="muted">
            {!demo.enabled || !demo.ok
              ? 'Prisma is taking a little break. She will be back soon!'
              : demo.viaFallback
                ? "You've used today's share of your rank. Prisma keeps helping, just a little slower until it refills."
                : 'Your AI game-dev companion, built into every project. Here is how much you have used.'}
          </p>
          {rank && (
            <>
              <Meter label="Messages today" used={demo.usage.messagesToday} limit={rank.limits.messagesPerDay} />
              <Meter label="Tokens today" used={demo.usage.tokensToday} limit={rank.limits.tokensPerDay} />
              <Meter label="Tokens this month" used={demo.usage.tokensMonth} limit={rank.limits.tokensPerMonth} />
            </>
          )}
          {demo.claude && superAvailable(account.choices) && (
            <>
              <Meter label="Super Prisma: this 5-hour window" used={demo.claude.fiveHour} limit={demo.claude.fiveHourLimit} unit="%" />
              <Meter label="Super Prisma: this week" used={demo.claude.weekly} limit={demo.claude.weeklyLimit} unit="%" />
            </>
          )}
          {superAvailable(account.choices) && <p className="muted">Your rank includes Super Prisma: switch it on in the chat for big ideas.</p>}
        </section>

        <section className="site-card">
          <h2>Username</h2>
          <p className="muted">The name everyone sees on your games, in the arcade and in the app. It has to be different from everyone else's.</p>
          <UsernameForm current={account.user.username} onSaved={() => { void load(); onChanged(); }} />
        </section>

        <section className="site-card">
          <h2>Storage</h2>
          <Meter label="Projects" used={account.storage.usedBytes / 1048576} limit={account.storage.limitBytes === null ? null : account.storage.limitBytes / 1048576} unit=" MB" />
          <p className="muted">
            {account.limits.projects === null ? 'Any number of projects' : `Up to ${account.limits.projects} projects`}; published games up to {account.limits.maxGameMb} MB each. Using {mb(account.storage.usedBytes)}.
          </p>
        </section>

        <section className="site-card">
          <h2>Prism Engine Cloud desktop app</h2>
          <p className="muted">
            Sign in to the desktop app with this account to open and edit your website games there, and to publish games here: in its Build window choose "Publish to Prism Engine Cloud Library", then approve the code it shows on this website.
          </p>
        </section>
      </div>

      <h2>Your published games</h2>
      {games.length === 0 ? (
        <p className="muted">Nothing published yet. Use Publish in a project's IDE.</p>
      ) : (
        <div className="game-grid">
          {games.map((g) => (
            <div key={g.slug} className="game-card-wrap">
              <GameCard game={g} />
              <button type="button" className="ghost danger" onClick={() => void deleteGame(g.slug)}>
                Remove from library
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="muted small">Signed in since {new Date(account.user.createdAt).toLocaleDateString()}.</p>
      <button className="ghost" type="button" onClick={onChanged}>
        Refresh
      </button>
    </div>
  );
}
